import {
  computed,
  onScopeDispose,
  ref,
  shallowRef,
  toValue,
  watch,
  type ComputedRef,
  type MaybeRefOrGetter,
  type Ref,
  type ShallowRef,
} from 'vue'

import { reportApi } from '../api/ReportApi'
import type {
  ReportDataLoader,
  ReportDataState,
  ReportResponse,
  ReportRow,
  ReportSpec,
} from '../models/report.types'
import { fetchReportData } from '../service/reportService'

import { useReportSchedules } from './useReportSchedules'

import { useAccount, useWorkspace } from '@/features/account'

export interface UseReportDataOptions {
  immediate?: boolean
  debounceMs?: number
  workspaceIds?: MaybeRefOrGetter<readonly (number | string)[]>
  exportRequest?: MaybeRefOrGetter<boolean>
  productPrecalculated?: MaybeRefOrGetter<boolean>
  /** Feature-owned sidecar orchestration around the standard report read (e.g. Benchmark). */
  dataLoader?: ReportDataLoader
}

export interface UseReportData {
  response: ShallowRef<ReportResponse | null>
  /** Exact request spec that produced `response`; remains stable while a replacement is pending. */
  settledSpec: ShallowRef<ReportSpec | null>
  rows: ComputedRef<ReportRow[]>
  state: Ref<ReportDataState>
  /** The HTTP boundary guarantees ApiError at runtime; kept opaque to avoid composable→http coupling. */
  error: ShallowRef<unknown | null>
  reload: () => Promise<void>
  cancel: () => void
}

/** Reactive, abort-safe module-08 report reader with explicit idle/loading/empty/error/ready states. */
export function useReportData(
  specSource: MaybeRefOrGetter<ReportSpec | null>,
  options: UseReportDataOptions = {},
): UseReportData {
  const account = useAccount()
  const workspace = useWorkspace()
  const schedules = useReportSchedules()
  const response = shallowRef<ReportResponse | null>(null)
  const settledSpec = shallowRef<ReportSpec | null>(null)
  const state = ref<ReportDataState>('idle')
  const error = shallowRef<unknown | null>(null)
  let activeController: AbortController | null = null
  let activeRequest = 0
  let debounceTimer: ReturnType<typeof setTimeout> | null = null

  const cancel = (): void => {
    activeRequest += 1
    activeController?.abort()
    activeController = null
    if (debounceTimer) clearTimeout(debounceTimer)
    debounceTimer = null
  }

  const reload = async (): Promise<void> => {
    const spec = toValue(specSource)
    const accountId = account.accountId.value
    const workspaceId = workspace.workspaceId.value
    if (!spec || accountId === undefined || workspaceId === undefined) {
      cancel()
      response.value = null
      settledSpec.value = null
      error.value = null
      state.value = 'idle'
      return
    }

    const workspaceIds = toValue(options.workspaceIds) ?? []
    const productPrecalculated =
      options.productPrecalculated === undefined
        ? Boolean(workspace.getAttribute('product_precalculated_config')?.value)
        : toValue(options.productPrecalculated)
    const schedule =
      spec.report_id === undefined || workspaceIds.length > 0 || spec.config_id !== undefined
        ? null
        : schedules.getScheduleByReportId(spec.report_id)
    const needsSchedule =
      spec.config_id === undefined &&
      workspaceIds.length === 0 &&
      !(productPrecalculated && spec.partition_name === 'metrics_product')
    if (needsSchedule && !schedule) {
      cancel()
      response.value = null
      settledSpec.value = snapshotReportSpec(spec)
      error.value = null
      state.value = 'empty'
      return
    }

    activeController?.abort()
    const controller = new AbortController()
    activeController = controller
    const requestId = ++activeRequest
    state.value = 'loading'
    error.value = null

    try {
      const load = (): Promise<ReportResponse> =>
        fetchReportData(
          spec,
          {
            accountId,
            workspaceId,
            workspaceIds,
            ...(schedule ? { scheduleId: schedule.id } : {}),
            productPrecalculated,
            exportRequest: toValue(options.exportRequest) ?? false,
            signal: controller.signal,
          },
          reportApi,
        )
      const next = options.dataLoader
        ? await options.dataLoader({ signal: controller.signal, load })
        : await load()
      if (requestId !== activeRequest || controller.signal.aborted) return
      response.value = next
      settledSpec.value = snapshotReportSpec(spec)
      state.value = reportRows(next).length === 0 ? 'empty' : 'ready'
    } catch (cause) {
      // Our own cancellation: this read was superseded or the caller went away. Leaving `state`
      // untouched is right — the replacement read owns it now.
      if (requestId !== activeRequest || controller.signal.aborted) return
      // A cancel we did NOT ask for (a shared in-flight read aborted by whoever opened it). Not
      // reporting it parks this reader at `loading` for good, which reads as a spinner that never
      // resolves; surface it so the four-state UX can offer its retry.
      response.value = null
      error.value = cause
      state.value = 'error'
    } finally {
      if (requestId === activeRequest) activeController = null
    }
  }

  const scheduleReload = (): void => {
    if (debounceTimer) clearTimeout(debounceTimer)
    const delay = options.debounceMs ?? 0
    if (delay > 0) debounceTimer = setTimeout(() => void reload(), delay)
    else void reload()
  }

  /**
   * Watch the request's value, not its computed object identity. DataVisible rebuilds a fresh
   * ReportSpec when presentation changes; Table and Card can therefore publish different objects
   * that ask the controller the exact same question. Canonical keys suppress that accidental
   * second read without retaining a settled response or changing explicit `reload()` semantics.
   */
  watch(
    () =>
      canonicalRequestKey({
        spec: toValue(specSource),
        accountId: account.accountId.value,
        workspaceId: workspace.workspaceId.value,
        workspaceIds: toValue(options.workspaceIds) ?? [],
        exportRequest: toValue(options.exportRequest) ?? false,
        productPrecalculated:
          options.productPrecalculated === undefined
            ? workspace.getAttribute('product_precalculated_config')?.value
            : toValue(options.productPrecalculated),
      }),
    scheduleReload,
    { immediate: options.immediate ?? true },
  )
  onScopeDispose(cancel)

  return {
    response,
    settledSpec,
    rows: computed(() => reportRows(response.value)),
    state,
    error,
    reload,
    cancel,
  }
}

function reportRows(response: ReportResponse | null): ReportRow[] {
  return response?.combined?.data ?? response?.data ?? []
}

function snapshotReportSpec(spec: ReportSpec): ReportSpec {
  return JSON.parse(JSON.stringify(spec)) as ReportSpec
}

function canonicalRequestKey(value: unknown): string {
  return JSON.stringify(canonicalize(value))
}

function canonicalize(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(canonicalize)
  if (typeof value !== 'object' || value === null) return value
  return Object.fromEntries(
    Object.entries(value)
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([key, entry]) => [key, canonicalize(entry)]),
  )
}
